import { defineStore } from 'pinia';
import type { AuthConfigDTO, Identity, Role } from '@its/shared';
import { api, ApiError } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { couperTempsReel } from '@/composables/useTempsReel';

/**
 * L'identité de l'utilisateur courant.
 *
 * LA SESSION DE LA BORNE EST UN COOKIE POSÉ PAR L'API, pas la session Supabase.
 * Supabase Auth ne sert qu'à PROUVER l'identité (Microsoft pour le groupe,
 * e-mail pour les clients) : le jeton obtenu est échangé une fois contre le
 * cookie, puis la session Supabase locale est fermée. Garder les deux ouvertes,
 * c'est avoir deux vérités sur « qui est connecté » — et une déconnexion qui
 * n'en ferme qu'une.
 *
 * `ready` ne vaut que pour le PREMIER chargement : ensuite l'état se met à jour
 * sur place, sans repasser par l'écran d'attente.
 */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as Identity | null,
    config: { sso: false, clientLogin: false, devLogin: false } as AuthConfigDTO,
    ready: false,
    loading: false,
  }),
  getters: {
    isAuthenticated: (s): boolean => s.user !== null,
    roles: (s): Role[] => s.user?.roles ?? [],
    isStaff: (s): boolean => s.user?.kind === 'staff',
  },
  actions: {
    hasRole(...attendus: Role[]): boolean {
      return this.roles.some((r) => attendus.includes(r));
    },

    async init(): Promise<void> {
      try {
        this.config = await api.get<AuthConfigDTO>('/auth/config');
      } catch {
        // API injoignable : l'écran de connexion dira qu'aucun mode n'est ouvert.
      }
      try {
        await this.fetchMe();
      } catch {
        // API injoignable : on montre la connexion (et la démo, qui n'a pas besoin du réseau)
        // plutôt que de laisser l'écran d'attente tourner sans fin.
        this.user = null;
      }
      this.ready = true;
    },

    async fetchMe(): Promise<void> {
      try {
        this.user = await api.get<Identity>('/me');
      } catch (err) {
        // 401 n'est pas une panne : c'est l'état « pas encore connecté ».
        if (err instanceof ApiError && err.status === 401) this.user = null;
        else throw err;
      }
    },

    /** Départ vers Microsoft ; le retour se fait sur /connexion/retour. */
    async loginWithMicrosoft(redirect: string): Promise<void> {
      const client = await supabase();
      if (!client) return;
      const retour = new URL('/connexion/retour', window.location.origin);
      retour.searchParams.set('redirect', redirect);
      await client.auth.signInWithOAuth({
        provider: 'azure',
        // `email` est indispensable : sans lui Entra ne rend pas l'adresse, et
        // l'API ne saurait pas qui rattacher.
        options: { scopes: 'openid email profile', redirectTo: retour.toString() },
      });
    },

    /** Connexion d'un compte client par e-mail et mot de passe. */
    async loginWithPassword(email: string, password: string): Promise<void> {
      const client = await supabase();
      if (!client) throw new Error("La connexion par e-mail n'est pas configurée.");
      this.loading = true;
      try {
        const { data, error } = await client.auth.signInWithPassword({ email, password });
        if (error || !data.session) throw new Error('Adresse ou mot de passe incorrect.');
        await this.exchange(data.session.access_token);
      } finally {
        this.loading = false;
      }
    },

    /** Au retour de Microsoft : la session Supabase est dans l'URL. */
    async completeRedirect(): Promise<void> {
      const client = await supabase();
      if (!client) throw new Error("La connexion Microsoft n'est pas configurée.");
      const { data } = await client.auth.getSession();
      if (!data.session) throw new Error('La connexion Microsoft a échoué. Réessayez.');
      await this.exchange(data.session.access_token);
    },

    /** Échange le jeton Supabase contre la session de la borne. */
    async exchange(accessToken: string): Promise<void> {
      try {
        this.user = await api.post<Identity>('/auth/session', { accessToken });
      } finally {
        const client = await supabase();
        await client?.auth.signOut({ scope: 'local' });
      }
    },

    async devLogin(email: string): Promise<void> {
      this.loading = true;
      try {
        this.user = await api.post<Identity>('/auth/dev-session', { email });
      } finally {
        this.loading = false;
      }
    },

    async logout(): Promise<void> {
      couperTempsReel();
      try {
        await api.post('/auth/logout');
      } finally {
        this.user = null;
      }
    },
  },
});
